import React, { ReactNode, SyntheticEvent, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { MAX_FILE_COUNT } from '../../../../constants'
import { CreationToolsContext } from '../../../../manager'
import CreationTool from '../../../../types/creation-tool'
import CreationToolError from '../../../../types/error'
import CreationToolGAEvent from '../../../../types/ga-event'
import CreationToolGradient from '../../../../types/gradient'
import CreationToolOutputType from '../../../../types/output-type'
import CreationToolWelcomeButton from '../../../../types/welcome-button'
import getSourceFrames from '../../../../utils/get-source-frames'
import getUnvalidatedSource from '../../../../utils/get-unvalidated-source'
import { validateSourceByInput } from '../../../../utils/validate-source'
import { CreationToolsWelcomeContext } from '../../context'

const FILE_BUTTON_Z_INDEX = 11

const Container = styled.div`
    border-radius: inherit;
    position: relative;

    input[type='file'] {
        cursor: pointer;
        height: 100%;
        left: 0;
        margin: 0;
        opacity: 0;
        padding: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }
`

const Gradient = styled.div`
    border-radius: inherit;
    height: 100%;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;

    &:after {
        background-image: repeating-linear-gradient(0deg, #fff, #fff 8px, transparent 8px, transparent 16px, #fff 16px),
            repeating-linear-gradient(90deg, #fff, #fff 8px, transparent 8px, transparent 16px, #fff 16px),
            repeating-linear-gradient(180deg, #fff, #fff 8px, transparent 8px, transparent 16px, #fff 16px),
            repeating-linear-gradient(270deg, #fff, #fff 8px, transparent 8px, transparent 16px, #fff 16px);
        background-size: 1px 100%, 100% 1px, 1px 100%, 100% 1px;
        background-position: 0 0, 0 0, 100% 0, 0 100%;
        background-repeat: no-repeat;
        border-radius: inherit;
        bottom: -8px;
        content: '';
        left: -8px;
        position: absolute;
        right: -8px;
        top: -8px;
    }
`

type Props = {
    children?: ReactNode
    className?: string
    button: CreationToolWelcomeButton
}

const CreationToolsWelcomeFileButton = ({ children, className, button }: Props) => {
    const { tool, setFiles, trackEvent } = useContext(CreationToolsContext)
    const { isDraggingFile, setError, setErrorSource } = useContext(CreationToolsWelcomeContext)
    const [isDraggedOver, setIsDraggedOver] = useState<boolean>(false)
    const outputType = CreationToolWelcomeButton.outputType(button)
    const mimeTypes = CreationToolWelcomeButton.mimeTypes(button)
    const gradientColors = isDraggedOver
        ? CreationToolGradient.colors(CreationTool.gradient(tool))
        : [`rgba(18, 18, 18, 0.15)`, `rgba(33, 33, 33, 0.15)`]

    const onDragEnter = () => {
        setIsDraggedOver(true)
    }

    const onDragLeave = () => {
        setIsDraggedOver(false)
    }

    const onFileInput = async (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const { files } = e.currentTarget

        if (!files || !files.length) return

        // check if too many files
        if (files.length > MAX_FILE_COUNT) {
            setError(CreationToolError.TooManyFiles)
            return
        }

        if (files.length === 1) {
            const file = files[0]

            try {
                const requiresFrameProcessing = CreationTool.requiresFrameProcessing(tool)
                // if the file will be processed into an array of frames, we don't need
                // to worry about validating audio or transparency. this will automatically
                // be accounted for via the tool's creation steps. (i.e. Cut Out)
                const validatedFile = await validateSourceByInput(file, mimeTypes, {
                    requiresAudio: !requiresFrameProcessing && CreationToolOutputType.requiresAudio(outputType),
                    requiresTransparency:
                        !requiresFrameProcessing && CreationToolOutputType.requiresTransparency(outputType),
                })
                let processedFile = validatedFile

                if (requiresFrameProcessing) {
                    const frames = await getSourceFrames(validatedFile)
                    processedFile.frames = frames
                }

                setFiles([processedFile], outputType)
            } catch (error) {
                trackEvent(CreationToolGAEvent.welcomeButtonCategory(button), 'Bad Format Error', file.type)
                setError(error as CreationToolError)
                setErrorSource(file)
            }
        } else {
            const sourceFiles = Array.from(files).map(getUnvalidatedSource)
            setFiles(sourceFiles, outputType)
        }

        const eventLabel = isDraggedOver ? 'Drag And Drop' : 'Browse Files'
        trackEvent(CreationToolGAEvent.welcomeButtonCategory(button), 'Input', eventLabel)
    }

    useEffect(() => {
        if (!isDraggingFile) {
            setIsDraggedOver(false)
        }
    }, [isDraggingFile])

    return (
        <Container
            {...{
                className,
                style: isDraggingFile ? { zIndex: FILE_BUTTON_Z_INDEX } : {},
                onDragEnter,
                onDragLeave,
            }}
            data-dragover={isDraggedOver && mimeTypes.length > 0}
        >
            {isDraggingFile && (
                <Gradient
                    style={{
                        background: `linear-gradient(45deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`,
                    }}
                />
            )}
            {children}
            <input
                type="file"
                accept={mimeTypes.join(',')}
                multiple={CreationTool.supportsMultipleFiles(tool)}
                onInput={onFileInput}
            />
        </Container>
    )
}

export default CreationToolsWelcomeFileButton
